import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, MessageSquare, Phone, ChevronDown } from 'lucide-react';

interface ContactUserDropdownProps {
  verification: {
    id: string;
    user_id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    organization_name: string | null;
  };
}

export const ContactUserDropdown: React.FC<ContactUserDropdownProps> = ({ verification }) => {
  const { toast } = useToast();
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSendProfileEmail = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.functions.invoke('send-profile-completion-email', {
        body: {
          email: verification.email,
          firstName: verification.first_name,
          lastName: verification.last_name,
          userId: verification.user_id
        }
      });

      if (error) throw error;

      toast({
        title: 'Email sent',
        description: 'Profile completion request sent successfully.'
      });
      setShowEmailDialog(false);
    } catch (error: any) {
      toast({
        title: 'Error sending email',
        description: error.message || 'Failed to send profile completion email.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartMessageThread = async () => {
    try {
      // Create a new message thread
      const { data, error } = await supabase
        .from('message_threads')
        .insert({
          title: `Profile Completion Required - ${verification.first_name || 'User'} ${verification.last_name || ''}`.trim(),
          thread_type: 'support',
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) throw error;

      // Add the user as a participant
      await supabase
        .from('message_participants')
        .insert({
          thread_id: data.id,
          user_id: verification.user_id
        });

      toast({
        title: 'Message thread created',
        description: 'You can now communicate with this user through the messaging system.'
      });
    } catch (error: any) {
      toast({
        title: 'Error creating message thread',
        description: error.message || 'Failed to create message thread.',
        variant: 'destructive'
      });
    }
  };

  const smsTemplate = `Hi ${verification.first_name || 'there'}, this is ThePEOApp admin. We need you to complete your profile information to process your PEO verification. Please log in to your account and fill out all required fields. Thanks!`;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      description: 'SMS template copied successfully.'
    });
    setShowSmsDialog(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="gap-1">
            <Mail className="h-4 w-4" />
            Contact
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setShowEmailDialog(true)}>
            <Mail className="h-4 w-4 mr-2" />
            Request Profile Completion
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleStartMessageThread}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Start Message Thread
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowSmsDialog(true)}>
            <Phone className="h-4 w-4 mr-2" />
            SMS Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Profile Completion Email Dialog */}
      <AlertDialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Profile Completion Request</AlertDialogTitle>
            <AlertDialogDescription>
              Send an email to {verification.email} requesting they complete their profile information for PEO verification.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendProfileEmail} disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SMS Template Dialog */}
      <AlertDialog open={showSmsDialog} onOpenChange={setShowSmsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>SMS Template</AlertDialogTitle>
            <AlertDialogDescription>
              Copy this template to send via your preferred SMS service:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="p-3 bg-muted rounded text-sm font-mono">
              {smsTemplate}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => copyToClipboard(smsTemplate)}>
              Copy to Clipboard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ContactUserDropdown;